import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';

type WizardStep = 'type' | 'tier' | 'connection' | 'bobs' | 'payment' | 'download' | 'verify';

interface WizardData {
  installationType: 'cloud' | 'self-hosted' | '';
  tier: 'starter' | 'pro' | 'enterprise' | '';
  odooUrl: string;
  odooApiKey: string;
  odooDatabase: string;
  selectedBobs: string[];
  paymentMethod: string;
  licenseKey: string;
  instanceId: string;
}

const steps: { id: WizardStep; title: string; description: string }[] = [
  { id: 'type', title: 'Installation Type', description: 'Choose how you want to deploy' },
  { id: 'tier', title: 'License Tier', description: 'Select your subscription plan' },
  { id: 'connection', title: 'Odoo Connection', description: 'Configure your Odoo instance' },
  { id: 'bobs', title: 'Select BoBs', description: 'Choose AI assistants to install' },
  { id: 'payment', title: 'Payment', description: 'Complete your subscription' },
  { id: 'download', title: 'Download', description: 'Get your license and installer' },
  { id: 'verify', title: 'Verify', description: 'Confirm installation success' },
];

export function InstanceWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>('type');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wizardData, setWizardData] = useState<WizardData>({
    installationType: '',
    tier: '',
    odooUrl: '',
    odooApiKey: '',
    odooDatabase: '',
    selectedBobs: [],
    paymentMethod: '',
    licenseKey: '',
    instanceId: '',
  });

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const canGoNext = () => {
    switch (currentStep) {
      case 'type':
        return !!wizardData.installationType;
      case 'tier':
        return !!wizardData.tier;
      case 'connection':
        return !!(wizardData.odooUrl && wizardData.odooApiKey && wizardData.odooDatabase);
      case 'bobs':
        return wizardData.selectedBobs.length > 0;
      case 'payment':
        return !!wizardData.paymentMethod;
      case 'download':
        return !!wizardData.licenseKey;
      case 'verify':
        return !!wizardData.instanceId;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1].id);
    } else {
      // Final step - navigate to instance detail
      navigate(`/client/instances/${wizardData.instanceId}`);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  const updateWizardData = (updates: Partial<WizardData>) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/client/dashboard')}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300 transition-colors mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-slate-100">Create New Instance</h1>
        <p className="mt-1 text-sm text-slate-400">
          Follow the steps to set up your Odoo instance with AI assistants
        </p>
      </div>

      {/* Progress Bar */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  index < currentStepIndex
                    ? 'bg-emerald-500 border-emerald-500'
                    : index === currentStepIndex
                    ? 'bg-blue-500 border-blue-500'
                    : 'bg-slate-700 border-slate-600'
                }`}
              >
                {index < currentStepIndex ? (
                  <Check className="h-5 w-5 text-white" />
                ) : (
                  <span className="text-sm font-semibold text-white">{index + 1}</span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-12 h-0.5 mx-2 transition-colors ${
                    index < currentStepIndex ? 'bg-emerald-500' : 'bg-slate-600'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-lg font-semibold text-slate-100">
            Step {currentStepIndex + 1}: {steps[currentStepIndex].title}
          </h3>
          <p className="text-sm text-slate-400">{steps[currentStepIndex].description}</p>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-8">
        {/* Step 1: Installation Type */}
        {currentStep === 'type' && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">Choose Installation Type</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <button
                onClick={() => updateWizardData({ installationType: 'cloud' })}
                className={`p-6 rounded-lg border-2 transition-all text-left ${
                  wizardData.installationType === 'cloud'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <h4 className="text-lg font-semibold text-slate-100 mb-2">Cloud Hosted</h4>
                <p className="text-sm text-slate-400">
                  We host and manage everything for you. Quick setup, automatic updates.
                </p>
                <div className="mt-4 text-xs text-slate-500">Recommended for most users</div>
              </button>
              <button
                onClick={() => updateWizardData({ installationType: 'self-hosted' })}
                className={`p-6 rounded-lg border-2 transition-all text-left ${
                  wizardData.installationType === 'self-hosted'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <h4 className="text-lg font-semibold text-slate-100 mb-2">Self-Hosted</h4>
                <p className="text-sm text-slate-400">
                  Deploy on your own infrastructure. Full control and customization.
                </p>
                <div className="mt-4 text-xs text-slate-500">Requires technical expertise</div>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: License Tier */}
        {currentStep === 'tier' && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">Select License Tier</h3>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                { id: 'starter', name: 'Starter', price: '$99/mo', bobs: 'Up to 5 BoBs', features: ['Basic support', 'Email notifications', 'Standard SLA'] },
                { id: 'pro', name: 'Professional', price: '$299/mo', bobs: 'Up to 20 BoBs', features: ['Priority support', 'Real-time alerts', '24/7 SLA'] },
                { id: 'enterprise', name: 'Enterprise', price: 'Custom', bobs: 'Unlimited BoBs', features: ['Dedicated support', 'Custom integrations', '99.9% uptime SLA'] },
              ].map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => updateWizardData({ tier: tier.id as any })}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    wizardData.tier === tier.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <h4 className="text-lg font-semibold text-slate-100">{tier.name}</h4>
                  <div className="text-2xl font-bold text-blue-400 my-2">{tier.price}</div>
                  <div className="text-sm text-emerald-400 mb-4">{tier.bobs}</div>
                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="text-xs text-slate-400 flex items-center gap-2">
                        <Check className="h-3 w-3 text-emerald-400" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Odoo Connection */}
        {currentStep === 'connection' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">Configure Odoo Connection</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Odoo URL
                </label>
                <input
                  type="url"
                  value={wizardData.odooUrl}
                  onChange={(e) => updateWizardData({ odooUrl: e.target.value })}
                  placeholder="https://your-company.odoo.com"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Database Name
                </label>
                <input
                  type="text"
                  value={wizardData.odooDatabase}
                  onChange={(e) => updateWizardData({ odooDatabase: e.target.value })}
                  placeholder="your_database_name"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  API Key
                </label>
                <input
                  type="password"
                  value={wizardData.odooApiKey}
                  onChange={(e) => updateWizardData({ odooApiKey: e.target.value })}
                  placeholder="Enter your Odoo API key"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Generate an API key in Odoo Settings → Users → Your User → Security
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: BoB Selection - Placeholder */}
        {currentStep === 'bobs' && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">Select BoBs (AI Assistants)</h3>
            <p className="text-slate-400">
              BoB selection interface will be implemented here. For now, proceed to next step.
            </p>
            <button
              onClick={() => updateWizardData({ selectedBobs: ['default-bob'] })}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
            >
              Select Default BoB
            </button>
          </div>
        )}

        {/* Step 5: Payment - Placeholder */}
        {currentStep === 'payment' && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">Payment</h3>
            <p className="text-slate-400">
              Payment integration (Stripe) will be implemented here. For now, proceed to next step.
            </p>
            <button
              onClick={() => updateWizardData({ paymentMethod: 'mock-payment' })}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
            >
              Proceed with Mock Payment
            </button>
          </div>
        )}

        {/* Step 6: Download - Placeholder */}
        {currentStep === 'download' && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">Download License & Installer</h3>
            <p className="text-slate-400">
              License key and installation script download will be implemented here.
            </p>
            <button
              onClick={() => updateWizardData({ licenseKey: 'mock-license-key' })}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
            >
              Generate Mock License
            </button>
          </div>
        )}

        {/* Step 7: Verify - Placeholder */}
        {currentStep === 'verify' && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">Verify Installation</h3>
            <p className="text-slate-400">
              Health check polling and verification will be implemented here.
            </p>
            <button
              onClick={() => updateWizardData({ instanceId: 'mock-instance-id' })}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
            >
              Complete Setup
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentStepIndex === 0}
          className="flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          Previous
        </button>
        <button
          onClick={handleNext}
          disabled={!canGoNext() || isSubmitting}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Processing...
            </>
          ) : currentStepIndex === steps.length - 1 ? (
            <>
              Complete Setup
              <Check className="h-5 w-5" />
            </>
          ) : (
            <>
              Next
              <ChevronRight className="h-5 w-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
